import styles from "./ErrorScreen.module.scss";

/** A new deploy removes old hashed chunks; tabs opened before it fail to lazy-load them. */
function isStaleChunk(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported/i.test(message);
}

type Props = {
  error?: unknown;
  onRetry?: () => void;
  title?: string;
  message?: string;
};

export function ErrorScreen({ error, onRetry, title, message }: Props) {
  const stale = isStaleChunk(error);
  const retry = stale || !onRetry ? () => window.location.reload() : onRetry;

  return (
    <div className={styles.screen} role="alert">
      <span className={styles.mark} aria-hidden="true">A</span>
      <h1>{title ?? (stale ? "A new version is available" : "Something went wrong")}</h1>
      <p>
        {message ??
          (stale
            ? "Archi was updated while this page was open. Reload to continue."
            : "Sorry, this page couldn't load. Please try again. If it keeps happening, reload the page.")}
      </p>
      <div className={styles.actions}>
        <button type="button" className={styles.primary} onClick={retry}>
          {stale ? "Reload" : "Try again"}
        </button>
        <a className={styles.secondary} href="/">
          Go to home
        </a>
      </div>
      {import.meta.env.DEV && error instanceof Error ? <pre className={styles.detail}>{error.message}</pre> : null}
    </div>
  );
}

export function NotFoundScreen() {
  return (
    <div className={styles.screen}>
      <span className={styles.mark} aria-hidden="true">A</span>
      <h1>Page not found</h1>
      <p>This link may be mistyped, or the page has moved.</p>
      <div className={styles.actions}>
        <a className={styles.primary} href="/">
          Go to home
        </a>
        <a className={styles.secondary} href="/designs">
          Browse designs
        </a>
      </div>
    </div>
  );
}
